// InfoMap: the "information map" of the business. The consult's output is a
// MAP: the facts the assessment engine needs, grouped into conversation
// areas, each with a priority tier, a "done when" test and a gap signal.
// Defined by Wes and Claude from what the engine needs (Wes, Sept 28 2026);
// no workflow catalog, no workflow names, ever.
//
// Fact keys are the keys the ConsultationPackage uses (packages/package-schema).
import { z } from "zod";
import { DraftLabel, Id, behaviorText, requireUnique } from "./common";

export const Tier = z.enum(["MUST", "SHOULD", "IF_NATURAL"]);
export type Tier = z.infer<typeof Tier>;

export const InfoArea = z.object({
  id: Id,
  label: behaviorText(80),
  description: behaviorText(1000).optional(),
});
export type InfoArea = z.infer<typeof InfoArea>;

export const InfoFact = z.object({
  /** Stable key; the package's facts are keyed by it. */
  key: Id,
  label: behaviorText(120),
  /** InfoArea id. */
  area: Id,
  tier: Tier,
  /** What Otto must have heard for this fact to count as captured. */
  done_when: behaviorText(1000),
  /** What in the conversation says this fact is still missing. */
  gap_signal: behaviorText(1000),
  /** Tone examples for the coach; never read out as a list. */
  example_questions: z.array(behaviorText(300)).min(1).max(8),
  /** "list" facts hold one item per entry (tools, pains, roles), "scalar" one answer. */
  shape: z.enum(["scalar", "list"]),
});
export type InfoFact = z.infer<typeof InfoFact>;

export const InfoMap = z
  .object({
    draft_label: DraftLabel,
    areas: z.array(InfoArea).min(1).max(20),
    facts: z.array(InfoFact).min(1).max(200),
  })
  .superRefine((map, ctx) => {
    requireUnique(
      map.areas.map((a) => a.id),
      ctx,
      ["areas"],
      "area id",
    );
    requireUnique(
      map.facts.map((f) => f.key),
      ctx,
      ["facts"],
      "fact key",
    );
    const areaIds = new Set(map.areas.map((a) => a.id));
    map.facts.forEach((fact, index) => {
      if (!areaIds.has(fact.area)) {
        ctx.addIssue({ code: "custom", path: ["facts", index, "area"], message: `Unknown area: ${fact.area}` });
      }
    });
  });
export type InfoMap = z.infer<typeof InfoMap>;

export function factsByTier(map: InfoMap, tier: Tier): InfoFact[] {
  return map.facts.filter((f) => f.tier === tier);
}

export function factKeys(map: InfoMap): Set<string> {
  return new Set(map.facts.map((f) => f.key));
}
